import React, { useMemo } from 'react';
import { Row, Col } from 'react-bootstrap';
import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';
import { useClinicalReports } from '../../clinical-reports/ClinicalReportsContext';
import { formatMonthKey } from './reportUtils';
import ReportSectionHead from './ReportSectionHead';
import { HeadDoctorStatCard } from '../ui';
import {
  ClinicalBarChart,
  ClinicalLineChart,
  ReportChartCard,
  ReportsChartCol,
  ReportsChartGrid
} from './charts/ClinicalCharts';
import { CHART_PALETTE } from './charts/chartTheme';
import { lineChartData } from './charts/chartDataHelpers';

const COPY = HEAD_DOCTOR_COPY.REPORTS;

export default function HeadDoctorReportsOccupationalDiseasesPage() {
  const { data, summary, loading } = useClinicalReports();
  const byMonth = data?.consultations?.occupational_diseases_by_month || [];
  const monthlyCounts = byMonth.map((row) => Number(row.count) || 0);
  const peakValue = monthlyCounts.length ? Math.max(...monthlyCounts) : 0;
  const peakMonth = byMonth.find((row) => Number(row.count) === peakValue)?.month_key || null;
  const monthlyAverage = monthlyCounts.length
    ? Math.round(monthlyCounts.reduce((acc, value) => acc + value, 0) / monthlyCounts.length)
    : 0;

  const trendData = useMemo(
    () =>
      lineChartData(
        byMonth.map((row) => formatMonthKey(row.month_key)),
        [
          {
            label: COPY.CHART_OCCUPATIONAL_DISEASES,
            data: byMonth.map((row) => Number(row.count)),
            color: CHART_PALETTE[6],
            fill: true
          }
        ]
      ),
    [byMonth]
  );

  const barData = useMemo(
    () => ({
      labels: byMonth.map((row) => formatMonthKey(row.month_key)),
      datasets: [
        {
          label: COPY.CHART_OCCUPATIONAL_DISEASES,
          data: byMonth.map((row) => Number(row.count)),
          backgroundColor: CHART_PALETTE[6],
          borderRadius: 6,
          maxBarThickness: 40
        }
      ]
    }),
    [byMonth]
  );

  const empty = (summary.occupational_diseases || 0) === 0;

  return (
    <>
      <ReportSectionHead
        badge={COPY.SUB_OCCUPATIONAL_DISEASES}
        title={COPY.PAGE_OCCUPATIONAL_DISEASES_TITLE}
        description={COPY.PAGE_OCCUPATIONAL_DISEASES_DESC}
      />

      <Row className="g-3 mb-4">
        <Col sm={6} lg={4}>
          <HeadDoctorStatCard
            title={COPY.KPI_OCCUPATIONAL_DISEASES}
            value={summary.occupational_diseases || 0}
            icon="alertCircle"
            tone="rose"
            subtitle={COPY.KPI_OCCUPATIONAL_DISEASES_HINT}
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={4}>
          <HeadDoctorStatCard
            title="Pic mensuel"
            value={peakValue}
            icon="chartBar"
            tone="amber"
            subtitle={peakMonth ? formatMonthKey(peakMonth) : '—'}
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={4}>
          <HeadDoctorStatCard
            title="Moyenne mensuelle"
            value={monthlyAverage}
            icon="calendar"
            tone="slate"
            subtitle="MP par mois"
            loading={loading}
          />
        </Col>
      </Row>

      {loading ? (
        <div className="hd-reports-panel mb-4">
          <div className="hd-reports-loading">
            <span className="spinner-border spinner-border-sm" />
            <span>{COPY.LOADING}</span>
          </div>
        </div>
      ) : empty ? (
        <div className="hd-reports-panel mb-4">
          <p className="hd-reports-empty mb-0">{COPY.EMPTY_OCCUPATIONAL_DISEASES}</p>
        </div>
      ) : (
        <ReportsChartGrid>
          <ReportsChartCol lg={6}>
            <ReportChartCard
              title={COPY.CHART_OCCUPATIONAL_DISEASE_TREND_TITLE}
              subtitle={COPY.CHART_OCCUPATIONAL_DISEASE_TREND_DESC}
              height={300}
            >
              <ClinicalLineChart
                data={trendData}
                xLabel={COPY.CHART_AXIS_PERIOD}
                yLabel={COPY.CHART_AXIS_OCCUPATIONAL_DISEASES}
              />
            </ReportChartCard>
          </ReportsChartCol>
          <ReportsChartCol lg={6}>
            <ReportChartCard
              title={COPY.CHART_OCCUPATIONAL_DISEASE_BAR_TITLE}
              subtitle={COPY.CHART_OCCUPATIONAL_DISEASE_BAR_DESC}
              height={300}
            >
              <ClinicalBarChart
                data={barData}
                yLabel={COPY.CHART_AXIS_OCCUPATIONAL_DISEASES}
                xLabel={COPY.CHART_AXIS_PERIOD}
              />
            </ReportChartCard>
          </ReportsChartCol>
        </ReportsChartGrid>
      )}
    </>
  );
}
